import {
  abilityCost,
  createGame,
  gravityFactor,
  step,
  visibleNext,
  type GameCommand,
  type GameEvent,
  type GameState,
} from '@bioforge/game-core';
import {
  ApiErrorCode,
  type AttemptResponse,
  type AttemptResult,
  type BossId,
  type LevelView,
  type PublicBoss,
  type Settings,
  type SoloAbility,
} from '@bioforge/protocol';
import { gsap } from 'gsap';
import {
  BlurFilter,
  Container,
  Graphics,
  Rectangle,
  Text,
  type Application,
  type Ticker,
} from 'pixi.js';
import { ApiRequestError } from './api/api-client.js';
import { EncyclopediaScene } from './encyclopedia-scene.js';
import { Journal } from './api/journal.js';
import { isTextInput, keyToAbilitySlot, keyToCommand } from './input/keyboard.js';
import { TouchPad, attachGestures, type TouchCommand } from './input/touch.js';
import {
  ABILITY_NAMES,
  BOSS_DAMAGE_HINTS,
  BOSS_NAMES,
  BOSS_TAUNTS,
  BOSS_TITLES,
  SOLO_ABILITY_HINTS,
  VESSEL_ACCUSATIVE,
  VESSEL_NAMES,
  VESSEL_PREPOSITIONAL,
  type Taunt,
} from './labels.js';
import { reducedMotion } from './overlays/html.js';
import { listen, type AppContext, type Navigator, type Scene } from './scene.js';
import { tr } from './story/story.js';
import {
  DesignFrame,
  currentFrame,
  isPortrait,
  screenBackground,
  type FrameSize,
} from './ui/frame.js';
import { idle, microbeIdle, motionReduced, setReducedMotion } from './ui/motion.js';
import { artSprite } from './ui/textures.js';
import { COLOR, DESIGN_HEIGHT, DESIGN_WIDTH, textStyle } from './ui/theme.js';
import { NetChip, UiButton, panel, speechBubble, vertical } from './ui/widgets.js';
import { PauseDialog, ResultDialog } from './view/battle-dialogs.js';
import { abilityRing, capsuleRow, hpBar, keyFooter } from './view/battle-panels.js';
import {
  bossAbilityRows,
  formatScore,
  formatTime,
  microbeCounts,
  tauntAfter,
  type TauntState,
} from './view/battle.js';
import { publicBossOf } from './view/hud.js';
import { jarModelOf } from './view/jar-model.js';
import { JarView, jarSize, type JarSkin } from './view/jar-view.js';
import { skinOf } from './view/skins.js';

/** Отправка результата: идёт, засчитана сервером, отклонена или не удалась. */
type Submission =
  | { readonly kind: 'sending' }
  | { readonly kind: 'done'; readonly result: AttemptResult }
  | { readonly kind: 'rejected' }
  | { readonly kind: 'failed' };

/** Колонки макета: показатели слева, банка, босс (или задача уровня) справа. */
const LEFT_W = 262;
const RIGHT_W = 300;
const GAP = 48;
const MAIN_TOP = 84;
/** Банка помещается между «Следующей» и подсказками управления. */
const JAR_TOP = MAIN_TOP + 46;
const JAR_BOTTOM = 840;
const MAX_CELL = 38;

/** Клавиши способностей по слотам (SPEC.md, 15.2). */
const SLOT_KEYS = ['C', 'V'] as const;

const PINK_PANEL = { top: 0x4a1638, bottom: 0x1e0c24, border: 0x8a2e5e } as const;

const caps = (text: string, color: number = COLOR.mint) =>
  new Text({
    text: text.toUpperCase(),
    style: textStyle({ size: 11, display: true, weight: '800', color, letterSpacing: 1.5 }),
  });

const isSolo = (kind: string): kind is SoloAbility => kind === 'cryo' || kind === 'antibiotic';

/**
 * Уровень экспедиции по макету «Соло · Босс (v2)» (SPEC.md, 5.3 и 15.5.1): правила и seed выдаёт
 * сервер, партия идёт на клиенте, журнал принятых команд уходит на сервер, итог засчитывает сервер
 * после воспроизведения.
 */
export class SoloScene implements Scene {
  readonly view: DesignFrame;
  private state: GameState;
  private readonly journal = new Journal();
  private elapsed = 0;
  /** Время партии без пауз, мс. */
  private playMs = 0;
  private paused = false;
  private submission: Submission | null = null;
  private closed = false;

  /** Всё, кроме диалогов: размывается под паузой. */
  private readonly main = new Container();
  private readonly jarBox = new Container();
  private jar: JarView | null = null;
  private cell = MAX_CELL;
  /** Портретная раскладка телефона (SPEC.md, 16.3): сводка сверху, банка, кнопки снизу. */
  private portrait = false;
  /** Сводка портретной раскладки: очки, микробы, босс, способность. */
  private readonly hud = new Container();
  private hudKey = '';
  private bubbleKey = '';
  /** Надетые скины игрока (магазин). */
  private readonly skin: JarSkin;
  /** Награды за этого босса, полученные победой. */
  private rewards: string[] = [];
  private readonly left = new Container();
  private readonly right = new Container();
  private readonly bubble = new Container();
  private readonly nextChip = new Container();
  private readonly headerTools = new Container();
  private readonly fx = new Container();
  private dialog: PauseDialog | ResultDialog | null = null;
  private blurred = false;
  /** Энциклопедия, открытая из паузы поверх боя. */
  private library: EncyclopediaScene | null = null;
  private leftX = 0;
  private rightX = 0;
  private jarX = 0;
  private jarTop = JAR_TOP;
  /** Подписки и анимации текущей раскладки — снимаются при повороте экрана. */
  private readonly layoutStops: (() => void)[] = [];

  private taunt: Taunt = 'start';
  private tauntState: TauntState = { misses: 0 };
  private screamUntil = 0;
  private screamTimer: ReturnType<typeof setTimeout> | null = null;
  private leftKey = '';
  private rightKey = '';
  private nextKey = '';
  private readonly leftStops: (() => void)[] = [];
  private readonly rightStops: (() => void)[] = [];
  private readonly stops: (() => void)[] = [];
  private readonly onTick = (ticker: Ticker): void => this.update(ticker.deltaMS);

  constructor(
    private readonly app: Application,
    private readonly nav: Navigator,
    private readonly ctx: AppContext,
    private readonly level: LevelView,
    private readonly attempt: AttemptResponse,
  ) {
    // Правила — ровно те, что выдал сервер: он воспроизведёт партию с ними же.
    this.state = createGame({ seed: attempt.seed, rules: attempt.rules });
    this.skin = skinOf(ctx.shop);
    const frame = currentFrame();
    this.view = new DesignFrame(this.background(frame), frame);
    this.build();

    this.stops.push(
      listen('keydown', (e) => this.onKey(e)),
      // Поворот телефона: раскладка собирается заново, партия продолжается.
      listen('resize', () => {
        if (isPortrait() === this.portrait) return;
        const next = currentFrame();
        this.view.resize(next, this.background(next));
        this.build();
        this.render();
      }),
      // Ушли из окна — партия встаёт на паузу.
      listen('blur', () => {
        if (this.state.status === 'playing' && !this.paused) this.pause();
      }),
    );
    app.ticker.add(this.onTick);
    this.render();
  }

  private apply(command: GameCommand): void {
    if (this.state.status !== 'playing' || this.paused) return;
    const result = step(this.state, command);
    if (!result.accepted) return;
    this.state = result.state;
    this.journal.record(command);
    if (command.type === 'soft-drop' || command.type === 'hard-drop') {
      this.elapsed = 0;
    }
    this.react(result.events);
    if (this.state.status !== 'playing') this.submit();
    this.render();
  }

  private onKey(e: KeyboardEvent): void {
    if (isTextInput(e.target) || this.library) return;
    const ended = this.state.status !== 'playing';
    if (ended) {
      if (e.code === 'Enter' || e.code === 'Escape') this.nav.expedition();
      else if (e.code === 'KeyR') this.restart();
      return;
    }
    if (e.code === 'Escape' || e.code === 'KeyP') {
      if (this.paused) this.resume();
      else this.pause();
      return;
    }
    if (this.paused) {
      if (e.code === 'KeyR') this.restart();
      return;
    }
    const slot = keyToAbilitySlot(e.code);
    if (slot !== null) {
      this.apply({ type: 'use-ability', slot });
      return;
    }
    const command = keyToCommand(e.code);
    if (command) {
      e.preventDefault();
      this.apply({ type: command });
    }
  }

  /** «Заново» — новая попытка: сервер выдаст новый seed. */
  private restart(): void {
    this.nav.play(this.level);
  }

  /** Отправляет журнал; при обрыве связи клиент повторяет, пока игрок не уйдёт с экрана. */
  private submit(): void {
    this.submission = { kind: 'sending' };
    this.ctx.api
      .submitAttempt(this.attempt.attemptId, this.journal.toString(), () => this.closed)
      .then(
        (result) => {
          this.ctx.progress = result.progress;
          this.submission = { kind: 'done', result };
          // Победа засчитана — сцена после уровня (у босса — финал главы).
          if (result.outcome === 'won' && !this.closed) this.nav.celebrate(this.level);
          if (result.outcome === 'won' && this.level.bossId) this.collectRewards();
        },
        (error: unknown) => {
          const rejected =
            error instanceof ApiRequestError &&
            (error.code === ApiErrorCode.InvalidReplay ||
              error.code === ApiErrorCode.AttemptExpired);
          this.submission = { kind: rejected ? 'rejected' : 'failed' };
        },
      )
      .finally(() => {
        if (!this.closed) this.render();
      });
  }

  private update(deltaMs: number): void {
    if (this.state.status !== 'playing' || this.paused) {
      return;
    }
    this.playMs += deltaMs;
    this.elapsed += deltaMs;
    // «Цитокиновый шторм» и «Лихорадка» ускоряют падение; темп задаёт клиент по множителю движка.
    const interval = this.attempt.gravityMs / gravityFactor(this.state);
    while (this.elapsed >= interval && this.state.status === 'playing') {
      this.elapsed -= interval;
      this.apply({ type: 'tick' });
    }
  }

  // ——— Реакция на события партии: всплывающие числа, реплики, крик босса ———

  private react(events: readonly GameEvent[]): void {
    const boss = publicBossOf(this.state);
    const jar = this.jar;
    if (!jar) return;
    for (const event of events) {
      if (event.type === 'cells-cleared' && event.cells.length > 0) {
        const cx = event.cells.reduce((sum, c) => sum + c.x, 0) / event.cells.length;
        const cy = event.cells.reduce((sum, c) => sum + c.y, 0) / event.cells.length;
        const at = jar.cellCenter(cx, cy);
        const text = event.chain > 1 ? `+${event.points} ×${event.chain}` : `+${event.points}`;
        this.float(text, this.jarX + at.x, this.jarTop + at.y, COLOR.gold, 20);
      } else if (event.type === 'boss-damaged') {
        const at = jar.bossCenter();
        if (at)
          this.float(
            `−${event.amount}`,
            this.jarX + at.x + 40,
            this.jarTop + at.y - 20,
            COLOR.white,
            22,
          );
        else if (this.portrait) this.float(`−${event.amount}`, 600, 140, COLOR.white, 22);
        else this.float(`−${event.amount}`, this.rightX + 120, MAIN_TOP + 150, COLOR.white, 22);
        jar.flashBoss();
      } else if (event.type === 'boss-ability') {
        this.scream();
      }
    }
    if (boss) {
      const next = tauntAfter(events, boss, this.tauntState);
      this.tauntState = next.state;
      if (next.taunt) this.taunt = next.taunt;
    }
  }

  /** Всплывающее число над банкой; при «меньше движения» просто появляется и исчезает. */
  private float(text: string, x: number, y: number, color: number, size: number): void {
    const t = new Text({
      text,
      style: textStyle({ size, display: true, weight: '900', color, shadow: 3 }),
    });
    t.anchor.set(0.5);
    t.position.set(x, y);
    this.fx.addChild(t);
    const done = () => {
      if (!t.destroyed) t.destroy();
    };
    if (motionReduced()) {
      gsap.delayedCall(1.2, done);
      return;
    }
    gsap.fromTo(
      t,
      { y: y + 6, alpha: 0 },
      { y: y - 40, alpha: 1, duration: 0.35, ease: 'power2.out' },
    );
    gsap.to(t, { alpha: 0, duration: 0.7, delay: 0.9, onComplete: done });
  }

  /** Сработала способность босса: «ГРРААА!», банка трясётся. */
  private scream(): void {
    this.screamUntil = performance.now() + 1600;
    if (this.screamTimer) clearTimeout(this.screamTimer);
    this.screamTimer = setTimeout(() => {
      this.screamTimer = null;
      if (!this.closed) this.render();
    }, 1650);
    if (motionReduced()) return;
    gsap.killTweensOf(this.jarBox);
    this.jarBox.position.set(this.jarX, this.jarTop);
    gsap.to(this.jarBox, {
      x: this.jarX + 3,
      duration: 0.05,
      yoyo: true,
      repeat: 7,
      ease: 'sine.inOut',
      onComplete: () => this.jarBox.position.set(this.jarX, this.jarTop),
    });
  }

  private screaming(): boolean {
    return performance.now() < this.screamUntil;
  }

  /** Победа над боссом даёт предмет-награду (SPEC.md, 18.3): обновляем магазин и называем её. */
  private collectRewards(): void {
    const before = new Set(this.ctx.shop.owned);
    this.ctx.api.shop().then(
      (shop) => {
        this.ctx.shop = shop;
        this.rewards = shop.items
          .filter((item) => shop.owned.includes(item.id) && !before.has(item.id))
          .map((item) => tr(item.title));
        if (this.rewards.length > 0 && !this.closed) this.render();
      },
      () => undefined,
    );
  }

  // ——— Пауза и итог ———

  private pause(): void {
    this.paused = true;
    this.render();
  }

  private resume(): void {
    this.paused = false;
    this.render();
  }

  private saveSettings(settings: Settings): void {
    const before = reducedMotion(this.ctx.settings.accessibility.reducedMotion);
    this.ctx.settings = settings;
    const after = reducedMotion(settings.accessibility.reducedMotion);
    if (before !== after) setReducedMotion(after);
    this.pendingSettings = settings;
  }

  /** Быстрые настройки паузы уходят на сервер, когда игрок возвращается в игру. */
  private pendingSettings: Settings | null = null;

  private flushSettings(): void {
    const settings = this.pendingSettings;
    if (!settings) return;
    this.pendingSettings = null;
    this.ctx.api.saveSettings(settings).then(
      (saved) => (this.ctx.settings = saved),
      () => undefined,
    );
  }

  private openLibrary(): void {
    const library = new EncyclopediaScene({
      ctx: this.ctx,
      reducedMotion: reducedMotion(this.ctx.settings.accessibility.reducedMotion),
      onClose: () => {
        library.destroy();
        if (this.library === library) this.library = null;
      },
    });
    this.library = library;
    this.app.stage.addChild(library.view);
  }

  private renderDialog(): void {
    const ended = this.state.status !== 'playing';
    const want = ended ? 'result' : this.paused ? 'pause' : null;
    if (this.dialog && (want !== 'pause' || !(this.dialog instanceof PauseDialog))) {
      this.dialog.destroy();
      this.dialog = null;
    }
    if (!want) {
      if (this.blurred) {
        this.blurred = false;
        this.main.filters = [];
        this.flushSettings();
      }
      return;
    }
    if (!this.blurred) {
      this.blurred = true;
      this.main.filters = [new BlurFilter({ strength: 5 })];
    }
    if (this.dialog) return;
    this.dialog = want === 'pause' ? this.pauseDialog() : this.resultDialog();
    this.view.content.addChild(this.dialog);
  }

  private subtitle(): string {
    const base = `Глава ${this.level.chapter} · ${VESSEL_NAMES[this.level.vessel]} · уровень ${this.level.index}`;
    return this.level.bossId ? `${base} — ${BOSS_NAMES[this.level.bossId]}` : base;
  }

  private pauseDialog(): PauseDialog {
    const s = this.state;
    const boss = publicBossOf(s);
    const id = this.level.bossId;
    return new PauseDialog({
      subtitle: this.subtitle(),
      stats: [
        ['Время', formatTime(this.playMs)],
        ['Очки', s.score.toLocaleString('ru-RU')],
        boss ? ['Босс', `${boss.hp} / ${boss.maxHp}`] : ['Микробы', String(s.virusesRemaining)],
      ],
      boss:
        id && boss
          ? {
              id,
              name: BOSS_NAMES[id],
              line: BOSS_TAUNTS.pause,
              hint: BOSS_DAMAGE_HINTS[boss.placement],
            }
          : null,
      settings: this.ctx.settings,
      onResume: () => this.resume(),
      onRestart: () => this.restart(),
      onLibrary: () => this.openLibrary(),
      onExit: () => this.nav.expedition(),
      onSettings: (settings) => this.saveSettings(settings),
      frame: this.view.size,
    });
  }

  private resultDialog(): ResultDialog {
    const s = this.state;
    const won = s.status === 'won';
    const submission = this.submission;
    let status = 'Сервер проверяет результат…';
    const marks: string[] = [];
    if (submission?.kind === 'rejected')
      status = 'Сервер не принял результат — сыграйте уровень заново.';
    else if (submission?.kind === 'failed') status = 'Не удалось отправить результат.';
    else if (submission?.kind === 'done') {
      const r = submission.result;
      status = r.outcome === 'won' ? 'Победа засчитана.' : 'Результат засчитан.';
      if (r.record) marks.push('Новый рекорд уровня!');
      for (const reward of this.rewards) marks.push(`Награда: ${reward} — наденьте в магазине`);
      const opened = r.progress.unlocked.at(-1);
      if (r.outcome === 'won' && opened && opened !== this.level.id)
        marks.push(`Открыт уровень ${opened}`);
    }
    return new ResultDialog({
      won,
      reason: won
        ? `${VESSEL_NAMES[this.level.vessel]} ${this.level.vessel === 'bioreactor' ? 'очищен' : 'очищена'}`
        : s.lossReason === 'piece-limit'
          ? 'Закончились капсулы'
          : 'Капсуле некуда упасть — сосуд переполнен',
      stats: [
        ['Очки', s.score.toLocaleString('ru-RU')],
        ['Лучшая цепочка', `×${Math.max(1, s.bestChain)}`],
        ['Время', formatTime(this.playMs)],
      ],
      status,
      marks,
      busy: !submission || submission.kind === 'sending',
      onAgain: () => this.restart(),
      onLevels: () => this.nav.expedition(),
      frame: this.view.size,
    });
  }

  // ——— Раскладка ———

  private background(frame: FrameSize): Graphics {
    const boss = this.level.bossId !== null;
    if (frame.width < frame.height) {
      return screenBackground(
        0.5,
        0.45,
        [
          ...(boss ? [{ x: 600, y: 200, r: 260, color: 0xd94b8c, alpha: 0.22 }] : []),
          { x: 80, y: 160, r: 260, color: 0x36e2d0, alpha: 0.16 },
        ],
        frame,
      );
    }
    return screenBackground(0.5, 0.48, [
      ...(boss ? [{ x: 1120, y: 500, r: 280, color: 0xd94b8c, alpha: 0.24 }] : []),
      { x: 140, y: 180, r: 300, color: 0x36e2d0, alpha: 0.16 },
    ]);
  }

  /** Собирает раскладку под текущую ориентацию; состояние партии не трогает. */
  private build(): void {
    this.layoutStops.splice(0).forEach((stop) => stop());
    this.leftStops.splice(0).forEach((stop) => stop());
    this.rightStops.splice(0).forEach((stop) => stop());
    this.dialog?.destroy();
    this.dialog = null;
    this.blurred = false;
    this.main.filters = [];
    gsap.killTweensOf(this.jarBox);
    this.jar?.destroy();
    this.jar = null;
    const boxes = [this.left, this.right, this.bubble, this.nextChip, this.headerTools, this.hud];
    for (const box of [...boxes, this.fx]) {
      box.removeChildren().forEach((child) => child.destroy({ children: true }));
    }
    this.main.removeChildren();
    this.view.content.removeChildren().forEach((child) => {
      if (child !== this.main) child.destroy({ children: true });
    });
    this.leftKey = '';
    this.rightKey = '';
    this.nextKey = '';
    this.hudKey = '';
    this.bubbleKey = '';
    this.portrait = isPortrait();
    this.view.content.addChild(this.main);
    if (this.portrait) this.buildPortrait();
    else this.buildLandscape();
    // Жесты на банке работают в обеих раскладках (сенсорные экраны ноутбуков тоже).
    const size = jarSize(this.state.rules.width, this.state.rules.height, this.cell);
    this.layoutStops.push(
      attachGestures(
        this.jarBox,
        new Rectangle(0, 0, size.width, size.height),
        this.cell,
        (command) => this.apply({ type: command }),
      ),
    );
  }

  /** Банка в прямоугольнике: клетка — наибольшая, при которой она помещается. */
  private placeJar(top: number, width: number, height: number, x: number | null): void {
    const { width: cols, height: rows } = this.state.rules;
    const empty = jarSize(0, 0, 0);
    this.cell = Math.max(
      16,
      Math.min(
        this.portrait ? 50 : MAX_CELL,
        Math.floor((width - empty.width) / cols),
        Math.floor((height - empty.height) / rows),
      ),
    );
    const jarW = jarSize(cols, rows, this.cell).width;
    this.jarTop = top;
    this.jarX = x ?? (720 - jarW) / 2;
    this.jar = new JarView(this.cell);
    this.jarBox.position.set(this.jarX, this.jarTop);
    this.jarBox.addChild(this.jar);
  }

  private buildLandscape(): void {
    const { width, height } = this.state.rules;
    const cell = Math.min(
      MAX_CELL,
      Math.floor((JAR_BOTTOM - JAR_TOP - jarSize(0, 0, 0).height) / height),
    );
    const jarW = jarSize(width, height, cell).width;
    const total = LEFT_W + GAP + jarW + GAP + RIGHT_W;
    this.leftX = (DESIGN_WIDTH - total) / 2;
    this.rightX = this.leftX + LEFT_W + GAP + jarW + GAP;
    this.placeJar(JAR_TOP, jarW, JAR_BOTTOM - JAR_TOP, this.leftX + LEFT_W + GAP);
    this.left.position.set(this.leftX, MAIN_TOP + 30);
    this.right.position.set(this.rightX, MAIN_TOP);
    this.bubble.position.set(this.rightX, MAIN_TOP);
    this.main.addChild(this.left, this.jarBox, this.nextChip, this.right, this.bubble, this.fx);
    this.header();
    this.footer();
  }

  /** Телефон: шапка, сводка, «Следующая» и реплика, крупная банка, экранные кнопки. */
  private buildPortrait(): void {
    this.placeJar(256, 680, 1020 - 256, null);
    this.main.addChild(this.hud, this.jarBox, this.nextChip, this.bubble, this.fx);

    const root = this.view.content;
    const logo = artSprite('logo', 48);
    logo.position.set(22 + 24, 44);
    const boss = this.level.bossId !== null;
    const label = new Text({
      text: `Глава ${this.level.chapter} · ${boss ? 'босс' : `уровень ${this.level.index}`}`,
      style: textStyle({ size: 16, display: true, weight: '800' }),
    });
    const chipW = label.width + 40;
    const chip = panel({ width: chipW, height: 48, radius: 14, ...(boss ? PINK_PANEL : {}) });
    label.anchor.set(0.5);
    label.position.set(chipW / 2, 24);
    chip.addChild(label);
    chip.position.set((720 - chipW) / 2, 20);
    const pause = new UiButton({
      width: 64,
      height: 64,
      label: '',
      radius: 16,
      center: true,
      icon: { key: 'icon-pause', size: 26 },
      onTap: () => {
        if (this.state.status === 'playing' && !this.paused) this.pause();
      },
    });
    pause.accessibleTitle = 'Пауза';
    pause.position.set(720 - 22 - 64, 12);
    root.addChild(logo, chip, pause);

    const send = (type: TouchCommand) => () => this.apply({ type });
    const pad = new TouchPad(
      [
        [
          {
            icon: 'icon-pad-left',
            title: 'Влево',
            width: 124,
            repeat: true,
            onPress: send('move-left'),
          },
          {
            icon: 'icon-pad-right',
            title: 'Вправо',
            width: 124,
            repeat: true,
            onPress: send('move-right'),
          },
          { icon: '', title: '', width: 112, spacer: true, onPress: () => undefined },
          {
            icon: 'icon-pad-ccw',
            title: 'Повернуть против часовой',
            width: 124,
            onPress: send('rotate-counterclockwise'),
          },
          {
            icon: 'icon-pad-cw',
            title: 'Повернуть по часовой',
            width: 124,
            onPress: send('rotate-clockwise'),
          },
        ],
        [
          {
            icon: 'icon-pad-down',
            label: 'Ускорить',
            title: 'Ускорить',
            width: 330,
            repeat: true,
            onPress: send('soft-drop'),
          },
          {
            icon: 'icon-pad-drop',
            label: 'Сбросить',
            title: 'Сбросить',
            width: 330,
            onPress: send('hard-drop'),
          },
        ],
      ],
      720,
      96,
    );
    pad.position.set(0, 1040);
    this.main.addChild(pad);
  }

  /** Сводка телефона: очки, микробы, босс (или капсулы) и кнопка способности между кнопками. */
  private renderHud(): void {
    const s = this.state;
    const boss = publicBossOf(s);
    const ability = s.rules.abilities[0];
    const energy = s.energy[0] ?? 0;
    const key = JSON.stringify([
      s.score,
      s.virusesRemaining,
      boss?.hp,
      s.piecesLocked,
      energy,
      s.effects.frozen > 0,
      s.status,
    ]);
    if (key === this.hudKey) return;
    this.hudKey = key;
    this.leftStops.splice(0).forEach((stop) => stop());
    this.hud.removeChildren().forEach((child) => child.destroy({ children: true }));
    const tile = (x: number, w: number, title: string, value: string, pink = false) => {
      const box = panel({ width: w, height: 84, radius: 16, ...(pink ? PINK_PANEL : {}) });
      box.position.set(x, 96);
      const t = caps(title, pink ? COLOR.pink : COLOR.mint);
      t.position.set(14, 12);
      const v = new Text({
        text: value,
        style: textStyle({ size: pink ? 18 : 26, display: true, weight: '900', shadow: 3 }),
      });
      v.position.set(14, pink ? 40 : 32);
      box.addChild(t, v);
      this.hud.addChild(box);
      return box;
    };
    tile(22, 250, 'Очки', formatScore(s.score));
    tile(22 + 250 + 12, 150, 'Микробы', String(s.virusesRemaining));
    const x3 = 22 + 250 + 12 + 150 + 12;
    if (boss) {
      const box = tile(x3, 254, 'Босс', `${boss.hp}/${boss.maxHp}`, true);
      const bar = hpBar(boss.hp, boss.maxHp, 140, undefined, 20);
      bar.position.set(100, 44);
      box.addChild(bar);
    } else {
      const limit = s.rules.pieceLimit;
      tile(
        x3,
        254,
        'Капсулы',
        limit !== null ? `${s.piecesLocked}/${limit}` : String(s.piecesLocked),
      );
    }
    if (ability) {
      const cost = abilityCost(ability);
      const ready = energy >= cost && s.status === 'playing';
      const ring = abilityRing(ability, energy / cost, ready, 44, this.leftStops);
      ring.position.set(360, 1040 + 48);
      ring.eventMode = 'static';
      ring.cursor = 'pointer';
      ring.accessible = true;
      ring.accessibleType = 'button';
      ring.accessibleTitle = `${ABILITY_NAMES[ability]}${ready ? ', готово' : ''}`;
      ring.on('pointertap', () => this.apply({ type: 'use-ability', slot: 0 }));
      this.hud.addChild(ring);
    }
  }

  private header(): void {
    const root = this.view.content;
    const logo = artSprite('logo', 44);
    logo.position.set(40 + 22, 38);
    const bio = new Text({
      text: 'Bio',
      style: textStyle({ size: 24, display: true, weight: '900', color: COLOR.mint, shadow: 3 }),
    });
    const forge = new Text({
      text: 'BioForge',
      style: textStyle({ size: 24, display: true, weight: '900', shadow: 3 }),
    });
    bio.anchor.set(0, 0.5);
    forge.anchor.set(0, 0.5);
    bio.position.set(96, 36);
    // Слово целиком белым, «Bio» поверх мятным — без зазора между частями (как на главном экране).
    forge.position.set(96, 36);
    root.addChild(logo, forge, bio);

    // По центру: где идёт бой.
    const boss = this.level.bossId !== null;
    const chip = new Container();
    const label = new Text({
      text: `Глава ${this.level.chapter} · ${VESSEL_NAMES[this.level.vessel]} · ${boss ? 'босс' : `уровень ${this.level.index}`}`,
      style: textStyle({ size: 15, display: true, weight: '800', letterSpacing: 0.6 }),
    });
    const chipW = 20 + 18 + 12 + label.width + 20;
    chip.addChild(
      panel({
        width: chipW,
        height: 40,
        radius: 14,
        ...(boss ? PINK_PANEL : {}),
      }),
    );
    const crown = artSprite(boss ? 'icon-crown' : 'icon-map-light', 18);
    crown.position.set(20 + 9, 20);
    label.anchor.set(0, 0.5);
    label.position.set(20 + 18 + 12, 20);
    chip.addChild(crown, label);
    chip.position.set((DESIGN_WIDTH - chipW) / 2, 18);
    root.addChild(chip);

    this.headerTools.position.set(DESIGN_WIDTH - 40, 16);
    root.addChild(this.headerTools);
    this.renderTools();
  }

  /** Справа сверху: сеть, музыка, звуки, пауза. Музыка и звуки — быстрые выключатели. */
  private renderTools(): void {
    this.headerTools.removeChildren().forEach((child) => child.destroy({ children: true }));
    const sound = this.ctx.settings.sound;
    const toggle = (key: 'musicMuted' | 'effectsMuted') => {
      const next = {
        ...this.ctx.settings,
        sound: { ...this.ctx.settings.sound, [key]: !this.ctx.settings.sound[key] },
      };
      this.saveSettings(next);
      this.flushSettings();
      this.renderTools();
    };
    const tool = (icon: string, title: string, onTap: () => void) => {
      const b = new UiButton({
        width: 44,
        height: 44,
        label: '',
        radius: 12,
        center: true,
        icon: { key: icon, size: 20 },
        onTap,
      });
      b.accessibleTitle = title;
      return b;
    };
    const buttons = [
      tool(
        sound.musicMuted ? 'icon-music-off' : 'icon-music',
        `Музыка: ${sound.musicMuted ? 'выключена' : 'включена'}`,
        () => toggle('musicMuted'),
      ),
      tool(
        sound.effectsMuted ? 'icon-sound-off' : 'icon-sound',
        `Звуки: ${sound.effectsMuted ? 'выключены' : 'включены'}`,
        () => toggle('effectsMuted'),
      ),
      tool('icon-pause', 'Пауза', () => {
        if (this.state.status === 'playing' && !this.paused) this.pause();
      }),
    ];
    let x = 0;
    for (const b of buttons.reverse()) {
      x -= 44;
      b.x = x;
      x -= 10;
      this.headerTools.addChild(b);
    }
    const chip = new NetChip();
    this.headerTools.addChild(chip);
    this.layoutStops.push(
      this.ctx.connection.watch((state) => {
        chip.set(state);
        chip.x = x - chip.width;
      }),
    );
  }

  private footer(): void {
    const rows: [string[], string][] = [
      [['←', '→'], 'двигать'],
      [['Z', 'X'], 'вращать'],
      [['↓'], 'ускорить'],
      [['Пробел'], 'сбросить'],
      ...this.state.rules.abilities.map((_, i): [string[], string] => [
        [SLOT_KEYS[i] ?? '?'],
        'способность',
      ]),
      [['Esc'], 'пауза'],
    ];
    const bar = keyFooter(rows);
    bar.position.set((DESIGN_WIDTH - bar.width) / 2, DESIGN_HEIGHT - 18 - 33);
    this.main.addChild(bar);
  }

  private render(): void {
    const s = this.state;
    const boss = publicBossOf(s);
    const frost = s.effects.frozen > 0;
    this.jar?.render(
      jarModelOf({
        board: s.board,
        active: s.active,
        boss,
        frost,
        spawn: s.rules.spawn,
        skin: this.skin,
      }),
    );
    this.renderNext(frost);
    if (boss && this.level.bossId) this.renderBubble(this.level.bossId);
    if (this.portrait) {
      this.renderHud();
    } else {
      this.renderLeft();
      if (boss && this.level.bossId) this.renderBoss(boss, this.level.bossId);
      else this.renderGoal();
    }
    this.renderDialog();
  }

  /** «Следующая» над банкой: одна капсула, со «Сканером» — три. */
  private renderNext(frost: boolean): void {
    const next = visibleNext(this.state);
    const key = JSON.stringify(next);
    if (key === this.nextKey) return;
    this.nextKey = key;
    this.nextChip.removeChildren().forEach((child) => child.destroy({ children: true }));
    const s = 30;
    const title = caps('Следующая', COLOR.mint);
    const pieces = capsuleRow(next, s, frost, this.skin.capsule === 'candy');
    const width = 14 + title.width + 10 + next.length * (2 * s + 10) - 10 + 10;
    this.nextChip.addChild(
      new Graphics()
        .roundRect(0, 0, width, 40, 14)
        .fill({ color: COLOR.panelBottom, alpha: 0.85 })
        .stroke({ width: 2, color: COLOR.border }),
    );
    title.anchor.set(0, 0.5);
    title.position.set(14, 20);
    pieces.position.set(14 + title.width + 10, 5);
    this.nextChip.addChild(title, pieces);
    const jarW = jarSize(this.state.rules.width, this.state.rules.height, this.cell).width;
    if (this.portrait) this.nextChip.position.set(22, 196);
    else this.nextChip.position.set(this.jarX + (jarW - width) / 2, MAIN_TOP);
  }

  /** Слева: очки, микробы по видам, способность. */
  private renderLeft(): void {
    const s = this.state;
    const counts = microbeCounts(s.board, s.rules.colorCount);
    const abilities = s.rules.abilities.map((kind, i) => ({
      kind,
      energy: s.energy[i] ?? 0,
      cost: abilityCost(kind),
    }));
    const key = JSON.stringify([s.score, counts, abilities, s.effects.frozen > 0, s.status]);
    if (key === this.leftKey) return;
    this.leftKey = key;
    this.leftStops.splice(0).forEach((stop) => stop());
    this.left.removeChildren().forEach((child) => child.destroy({ children: true }));

    // Очки.
    const score = new Container();
    score.addChild(panel({ width: LEFT_W, height: 84, radius: 16 }));
    const scoreCaps = caps('Очки');
    scoreCaps.position.set(16, 14);
    const value = new Text({
      text: formatScore(s.score),
      style: textStyle({ size: 34, display: true, weight: '900', shadow: 3, lineHeight: 40 }),
    });
    value.position.set(16, 32);
    score.addChild(scoreCaps, value);
    this.left.addChild(score);

    // Микробы.
    const rowH = 48;
    const listH = 14 + 22 + 10 + counts.length * (rowH + 10) - 10 + 14;
    const microbes = new Container();
    microbes.y = 84 + 14;
    microbes.addChild(panel({ width: LEFT_W, height: listH, radius: 16 }));
    const mCaps = caps('Микробы');
    mCaps.position.set(16, 18);
    const total = new Text({
      text: String(counts.reduce((n, c) => n + c.count, 0)),
      style: textStyle({ size: 22, display: true, weight: '900' }),
    });
    total.anchor.set(1, 0);
    total.position.set(LEFT_W - 16, 10);
    microbes.addChild(mCaps, total);
    counts.forEach((row, i) => {
      const y = 14 + 22 + 10 + i * (rowH + 10);
      const art = artSprite(`microbe-${row.color}`, rowH);
      art.position.set(16 + rowH / 2, y + rowH / 2);
      this.leftStops.push(microbeIdle(art, row.color, row.color * 0.5));
      const entry = this.ctx.content.catalog.find((e) => e.id === `microbe-${row.color}`);
      const name = new Text({
        text: entry ? tr(entry.title) : `Микроб ${row.color + 1}`,
        style: textStyle({ size: 15, weight: '700' }),
      });
      name.position.set(16 + rowH + 12, y + 4);
      const kind = s.rules.bacteriaColors.includes(row.color) ? 'бактерия' : 'вирус';
      const note = new Text({
        text: row.shielded > 0 ? `${kind} · ${row.shielded} в биоплёнке` : kind,
        style: textStyle({ size: 12, color: COLOR.textMuted }),
      });
      note.position.set(16 + rowH + 12, y + 25);
      const count = new Text({
        text: `×${row.count}`,
        style: textStyle({ size: 15, display: true, weight: '800' }),
      });
      count.anchor.set(1, 0.5);
      count.position.set(LEFT_W - 16, y + rowH / 2);
      microbes.addChild(art, name, note, count);
    });
    this.left.addChild(microbes);

    // Способности по слотам.
    let y = microbes.y + listH + 14;
    abilities.forEach((a, slot) => {
      const box = this.abilityPanel(a.kind, a.energy, a.cost, slot);
      box.y = y;
      y += box.height + 14;
      this.left.addChild(box);
    });
  }

  private abilityPanel(
    kind: GameState['rules']['abilities'][number],
    energy: number,
    cost: number,
    slot: number,
  ): Container {
    const box = new Container();
    const ready = energy >= cost && this.state.status === 'playing';
    const frozen = this.state.effects.frozen;
    box.addChild(
      panel({
        width: LEFT_W,
        height: 104,
        radius: 16,
        top: 0x183a5c,
        bottom: 0x0c1a36,
        border: 0x7fd8ff,
      }),
    );
    // Кольцо заряда с кнопкой: касание применяет способность, как клавиша.
    const progress = Math.max(0, Math.min(1, energy / cost));
    const ring = abilityRing(kind, progress, ready, 38, this.leftStops);
    ring.position.set(16 + 38, 52);
    ring.eventMode = 'static';
    ring.cursor = ready ? 'pointer' : 'default';
    ring.accessible = true;
    ring.accessibleType = 'button';
    const name = ABILITY_NAMES[kind];
    const keyName = SLOT_KEYS[slot] ?? '?';
    ring.accessibleTitle = `${name}, клавиша ${keyName}${ready ? ', готово' : ''}`;
    ring.on('pointertap', () => this.apply({ type: 'use-ability', slot }));
    box.addChild(ring);

    const tx = 16 + 76 + 14;
    const title = new Text({
      text: name,
      style: textStyle({ size: 14, display: true, weight: '800' }),
    });
    title.position.set(tx, 16);
    const hint = new Text({
      text: isSolo(kind) ? SOLO_ABILITY_HINTS[kind] : '',
      style: textStyle({ size: 13, color: COLOR.textSoft, wrap: LEFT_W - tx - 12, lineHeight: 17 }),
    });
    hint.position.set(tx, 38);
    const state =
      kind === 'cryo' && frozen > 0
        ? `Иней · ещё ${frozen}`
        : ready
          ? `Готово · ${keyName}`
          : `Заряд ${Math.round(progress * 100)}% · ${keyName}`;
    const status = caps(state, ready || frozen > 0 ? 0x7fd8ff : COLOR.textMuted);
    status.position.set(tx, 78);
    box.addChild(title, hint, status);
    return box;
  }

  /** Справа без босса: задача уровня и ход партии. */
  private renderGoal(): void {
    const s = this.state;
    const best = this.ctx.progress.levels[this.level.id]?.bestScore ?? null;
    const key = JSON.stringify([
      s.virusesRemaining,
      s.piecesLocked,
      s.lastChain,
      s.bestChain,
      best,
    ]);
    if (key === this.rightKey) return;
    this.rightKey = key;
    this.right.removeChildren().forEach((child) => child.destroy({ children: true }));
    const rows: [string, string][] = [
      [
        'Капсулы',
        s.rules.pieceLimit !== null
          ? `${s.piecesLocked} / ${s.rules.pieceLimit}`
          : String(s.piecesLocked),
      ],
      ['Цепочка', s.lastChain > 0 ? `×${s.lastChain}` : '—'],
      ['Лучшая цепочка', s.bestChain > 0 ? `×${s.bestChain}` : '—'],
      ['Рекорд уровня', best !== null ? best.toLocaleString('ru-RU') : '—'],
    ];
    const height = 16 + 18 + 34 + 46 + rows.length * 34 + 10;
    const box = new Container();
    box.y = 30;
    box.addChild(panel({ width: RIGHT_W, height, radius: 18 }));
    const kicker = caps('Задача');
    kicker.position.set(16, 16);
    const title = new Text({
      text: `Очистите ${VESSEL_ACCUSATIVE[this.level.vessel]}`,
      style: textStyle({ size: 22, display: true, weight: '900', shadow: 3 }),
    });
    // «Очистите чашку Петри» длиннее панели — уменьшаем шрифт, а не переносим строку.
    if (title.width > RIGHT_W - 32) title.scale.set((RIGHT_W - 32) / title.width);
    title.position.set(16, 34);
    const sub = new Text({
      text: `Осталось микробов: ${s.virusesRemaining}`,
      style: textStyle({ size: 14, color: COLOR.textSoft }),
    });
    sub.position.set(16, 72);
    box.addChild(kicker, title, sub);
    rows.forEach(([k, v], i) => {
      const y = 16 + 18 + 34 + 46 + i * 34;
      const line = new Graphics()
        .moveTo(16, y - 6)
        .lineTo(RIGHT_W - 16, y - 6)
        .stroke({ width: 1, color: COLOR.borderDim });
      const kt = new Text({ text: k, style: textStyle({ size: 14, color: COLOR.textMuted }) });
      kt.position.set(16, y);
      const vt = new Text({
        text: v,
        style: textStyle({ size: 15, display: true, weight: '800' }),
      });
      vt.anchor.set(1, 0);
      vt.position.set(RIGHT_W - 16, y);
      box.addChild(line, kt, vt);
    });
    this.right.addChild(box);
  }

  /** Справа с боссом: реплика, карточка со здоровьем, способности с отсчётом. */
  /** Реплика босса или его крик («ГРРААА!»): справа над карточкой, на телефоне — над банкой. */
  private renderBubble(id: BossId): void {
    const scream = this.screaming();
    const key = JSON.stringify([scream, this.taunt, this.portrait]);
    if (key === this.bubbleKey) return;
    this.bubbleKey = key;
    this.bubble.removeChildren().forEach((child) => child.destroy({ children: true }));
    const zone = new Container();
    if (scream) {
      const g = new Graphics();
      const pts = [
        10, 60, 32, 44, 18, 20, 52, 30, 64, 6, 86, 28, 112, 4, 128, 30, 160, 10, 166, 36, 204, 22,
        192, 50, 222, 62, 192, 74, 206, 100, 168, 88, 152, 114, 128, 92, 100, 116, 88, 90, 54, 106,
        58, 80, 18, 86, 36, 68,
      ];
      g.poly(pts.map((v, i) => (i % 2 === 0 ? (v * 260) / 230 : (v * 96) / 120)))
        .fill(COLOR.gold)
        .stroke({ width: 4, color: COLOR.ink, join: 'round' });
      const t = new Text({
        text: 'ГРРААА!',
        style: textStyle({ size: 30, display: true, weight: '900', color: COLOR.rose, shadow: 2 }),
      });
      t.anchor.set(0.5);
      t.position.set(130, 48);
      zone.addChild(g, t);
      if (this.portrait) zone.scale.set(0.8);
    } else {
      const bubble = speechBubble(
        BOSS_NAMES[id],
        BOSS_TAUNTS[this.taunt],
        this.portrait ? 300 : 280,
        'bottom',
      );
      bubble.y = 96 - 14 - bubble.height;
      zone.addChild(bubble);
      this.layoutStops.push(idle(bubble, 'talk'));
    }
    this.bubble.addChild(zone);
    if (this.portrait) this.bubble.position.set(720 - 22 - zone.width, 160);
  }

  private renderBoss(boss: PublicBoss, id: BossId): void {
    const scream = this.screaming();
    const rows = bossAbilityRows(this.state, boss);
    const key = JSON.stringify([boss, rows, scream, this.taunt]);
    if (key === this.rightKey) return;
    this.rightKey = key;
    this.rightStops.splice(0).forEach((stop) => stop());
    this.right.removeChildren().forEach((child) => child.destroy({ children: true }));

    // Карточка босса.
    const card = new Container();
    card.y = 106;
    const segW = RIGHT_W - 32;
    const hint = new Text({
      text: BOSS_DAMAGE_HINTS[boss.placement],
      style: textStyle({ size: 13, color: 0xe2b8cc, wrap: segW, lineHeight: 18 }),
    });
    const status =
      boss.dormant > 0
        ? `Спит и неуязвим ещё ${boss.dormant} капс.`
        : boss.invulnerable
          ? 'Неуязвим, пока живы сторожа (золотая биоплёнка)'
          : '';
    const statusText = new Text({
      text: status,
      style: textStyle({
        size: 13,
        weight: '700',
        color: COLOR.goldSoft,
        wrap: segW,
        lineHeight: 18,
      }),
    });
    const cardH =
      16 + 96 + 14 + 20 + 6 + 24 + 8 + hint.height + (status ? 6 + statusText.height : 0) + 16;
    card.addChild(panel({ width: RIGHT_W, height: cardH, radius: 18, ...PINK_PANEL }));
    const tile = new Graphics()
      .roundRect(16, 16, 96, 96, 18)
      .fill(vertical(0x5a1e46, 0x24091f))
      .stroke({ width: 2, color: COLOR.ink });
    const face = artSprite(`boss-${id}${scream ? '-scream' : ''}`, 88);
    face.position.set(16 + 48, 16 + 48);
    this.rightStops.push(idle(face, 'boss'));
    const where =
      boss.placement === 'vessel'
        ? `Босс · в ${VESSEL_PREPOSITIONAL[this.level.vessel]}`
        : 'Босс · за стеклом';
    const kicker = caps(where, COLOR.pink);
    kicker.position.set(16 + 96 + 14, 30);
    const name = new Text({
      text: BOSS_NAMES[id],
      style: textStyle({ size: 22, display: true, weight: '900', shadow: 3, wrap: RIGHT_W - 140 }),
    });
    name.position.set(16 + 96 + 14, 48);
    const title = new Text({
      text: BOSS_TITLES[id],
      style: textStyle({ size: 13, color: 0xe2b8cc }),
    });
    title.position.set(16 + 96 + 14, 48 + name.height + 2);
    card.addChild(tile, face, kicker, name, title);

    let y = 16 + 96 + 14;
    const hpLabel = new Text({ text: 'Здоровье', style: textStyle({ size: 13, color: 0xe2b8cc }) });
    hpLabel.position.set(16, y);
    const hpValue = new Text({
      text: `${boss.hp} / ${boss.maxHp}`,
      style: textStyle({ size: 14, display: true, weight: '800' }),
    });
    hpValue.anchor.set(1, 0);
    hpValue.position.set(RIGHT_W - 16, y);
    y += 26;
    const bar = hpBar(boss.hp, boss.maxHp, segW);
    bar.position.set(16, y);
    card.addChild(hpLabel, hpValue, bar);
    y += 24 + 8;
    hint.position.set(16, y);
    card.addChild(hint);
    y += hint.height;
    if (status) {
      statusText.position.set(16, y + 6);
      card.addChild(statusText);
    }
    this.right.addChild(card);

    // Способности босса.
    const list = new Container();
    list.y = card.y + cardH + 12;
    const kickerA = caps('Способности босса', COLOR.pink);
    kickerA.position.set(16, 16);
    list.addChild(kickerA);
    let ay = 16 + 14 + 14;
    const textW = RIGHT_W - 32 - 36 - 12;
    for (const row of rows) {
      const item = new Container();
      item.position.set(16, ay);
      item.alpha = row.active ? 1 : 0.6;
      item.addChild(
        new Graphics()
          .roundRect(0, 0, 36, 36, 10)
          .fill(row.active ? 0xff4f7b : 0x3a1a34)
          .stroke({ width: 2, color: COLOR.ink }),
      );
      const icon = artSprite(`icon-boss-${row.kind}`, 20);
      icon.position.set(18, 18);
      const nameT = new Text({ text: row.name, style: textStyle({ size: 15, weight: '700' }) });
      nameT.position.set(48, 0);
      const when = new Text({
        text: row.status,
        style: textStyle({
          size: 13,
          color: 0xffb3c8,
          wrap: textW - nameT.width - 8,
          align: 'right',
        }),
      });
      when.anchor.set(1, 0);
      when.position.set(48 + textW, 2);
      item.addChild(icon, nameT, when);
      let iy = Math.max(22, when.height + 4);
      if (row.pips) {
        const pips = new Graphics();
        const pw = Math.min(22, (textW - 4 * (row.pips.total - 1)) / row.pips.total);
        for (let k = 0; k < row.pips.total; k++) {
          pips.roundRect(48 + k * (pw + 4), iy + 2, pw, 8, 4);
          if (k < row.pips.filled) pips.fill(0xff4f7b);
          else pips.fill({ color: 0xff8fb0, alpha: 0.18 });
        }
        item.addChild(pips);
        iy += 16;
      }
      const entry = this.ctx.content.catalog.find((e) => e.id === `boss-${row.kind}`);
      if (entry) {
        const what = new Text({
          text: tr(entry.what),
          style: textStyle({ size: 13, color: 0xe2b8cc, wrap: textW, lineHeight: 18 }),
        });
        what.position.set(48, iy + 2);
        item.addChild(what);
        iy += what.height + 2;
      }
      list.addChild(item);
      ay += Math.max(36, iy) + 12;
    }
    const effects: string[] = [];
    if (boss.fog) effects.push(`Туман: ещё ${boss.fog.capsulesLeft} капс.`);
    if (boss.storm > 0) effects.push(`Шторм: капсулы падают быстрее ещё ${boss.storm} капс.`);
    for (const line of effects) {
      const t = new Text({
        text: line,
        style: textStyle({ size: 13, weight: '700', color: COLOR.goldSoft, wrap: RIGHT_W - 32 }),
      });
      t.position.set(16, ay);
      list.addChild(t);
      ay += t.height + 6;
    }
    // Список не должен уходить под подсказки управления: лишнее обрезается.
    const listH = Math.min(ay + 4, JAR_BOTTOM - MAIN_TOP - list.y);
    list.addChildAt(
      new Graphics()
        .roundRect(0, 0, RIGHT_W, listH, 16)
        .fill({ color: 0x1e0c24, alpha: 0.88 })
        .stroke({ width: 2, color: 0x5e2346 }),
      0,
    );
    if (ay + 4 > listH) {
      const mask = new Graphics().roundRect(0, 0, RIGHT_W, listH, 16).fill(COLOR.white);
      list.addChild(mask);
      list.mask = mask;
    }
    this.right.addChild(list);
  }

  destroy(): void {
    this.closed = true;
    this.app.ticker.remove(this.onTick);
    if (this.screamTimer) clearTimeout(this.screamTimer);
    this.flushSettings();
    this.library?.destroy();
    this.stops.splice(0).forEach((off) => off());
    this.leftStops.splice(0).forEach((stop) => stop());
    this.rightStops.splice(0).forEach((stop) => stop());
    gsap.killTweensOf(this.jarBox);
    for (const child of this.fx.children) gsap.killTweensOf(child);
    this.dialog?.destroy();
    this.view.destroy();
  }
}
